import { useState, useEffect } from 'react'
import { useAdmin } from '../context/AdminContext'
import { createAdminApi, DbCachedResponseDetail } from '../services/adminApi'
import styles from './PrefabsPage.module.css'

const truncate = (s: string, max: number) => (s.length > max ? s.slice(0, max) + '…' : s)

export default function CachedResponsesPage() {
  const { apiKey } = useAdmin()

  const [items, setItems] = useState<DbCachedResponseDetail[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [createError, setCreateError] = useState('')

  useEffect(() => {
    const api = createAdminApi()
    setLoading(true)
    setError('')
    api.listDbCachedResponses()
      .then(setItems)
      .catch(e => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false))
  }, [apiKey])

  async function handleCreate() {
    if (!newName.trim()) return
    const api = createAdminApi()
    setCreateError('')
    try {
      const item = await api.createDbCachedResponse(newName.trim(), [], '', [], '')
      window.location.hash = '#cached-responses/' + item.id
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : String(e))
    }
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1>Cached Responses</h1>
        <div className={styles.headerStats}>
          {!loading && <span className={styles.statPill}>{items.length} in DB</span>}
          <button className={styles.createBtn} onClick={() => { setCreating(p => !p); setCreateError('') }}>
            + New Cached Response
          </button>
        </div>
      </header>

      {creating && (
        <div className={styles.createBar}>
          <input
            className={styles.createInput}
            placeholder="Cached response name (unique ID)"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleCreate() }}
            autoFocus
          />
          <button className={styles.saveBtn} onClick={handleCreate}>Create</button>
          <button className={styles.cancelBtn} onClick={() => { setCreating(false); setNewName(''); setCreateError('') }}>Cancel</button>
          {createError && <span className={styles.saveError}>{createError}</span>}
        </div>
      )}

      {error && <div className={styles.error}>{error}</div>}

      <div className={styles.content}>
        {loading ? (
          <p className={styles.empty}>Loading…</p>
        ) : items.length === 0 ? (
          <p className={styles.empty}>No cached responses in DB. Create one above.</p>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Phrases</th>
                <th>Response</th>
                <th>Stories</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr
                  key={item.id}
                  className={styles.row}
                  onClick={() => { window.location.hash = '#cached-responses/' + item.id }}
                >
                  <td className={styles.nameCell}>
                    <a href={'#cached-responses/' + item.id} onClick={e => e.stopPropagation()}>
                      {item.name}
                    </a>
                  </td>
                  <td>{item.phrases.length ? truncate(item.phrases.join(' · '), 60) : '—'}</td>
                  <td>{item.response ? truncate(item.response, 80) : '—'}</td>
                  <td>{item.stories.length} {item.stories.length === 1 ? 'story' : 'stories'}</td>
                  <td>{new Date(item.updatedAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
